// React
import React, { useEffect, useState, useMemo, useCallback } from "react";

// Routing
import {
  useNavigate,
  useOutletContext,
  useSearchParams,
} from "react-router-dom";

// API + Hooks + Helpers
import api from "../../../services/api/axios";
import useCompaniesData from "./hooks/useCompaniesData";
import useDebouncedValue from "../../../hooks/useDebouncedValue";
import useRowSelection from "../../../hooks/useRowSelection";
import {
  downloadBlobFile,
  normalizePipelineStageFilter,
  PIPELINE_STAGE_OPTIONS,
} from "./BRECompanyHelpers";
import {
  exportBRECompanies,
  updateBRECompanyPipelineStage,
} from "./BRECompanyAPI";
import {
  normalizeCity,
  normalizeString,
} from "../../../utils/filterNormalization";

// MUI
import {
  Box,
  Button,
  Card,
  Snackbar,
  Alert,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";

// Icons
import BusinessIcon from "@mui/icons-material/Business";
import AddIcon from "@mui/icons-material/Add";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

// Components
import SearchBar from "../../../shared/components/SearchBar";
import EntityProfile from "../../../shared/components/EntityProfile";
import DeleteConfirmDialog from "../../../shared/components/DeleteConfirmDialog";
import CompaniesModuleSwitcher from "../../../shared/components/CompaniesModuleSwitcher";
import BRECompaniesTable from "./BRECompaniesTable";
import CompanyImportDialog from "./CompanyImportDialog";

// Config
import { companyProfileConfig } from "../../../shared/profile-configs/CompanyProfileConfig";

// Pagination
import BrandPagination from "../../../layouts/Pagination";
import {
  ACTIONS_HEADER_BTN_SX,
  ACTION_MENU_PAPER_SX,
} from "../../../theme/buttonstyles";

const BRAND_BLUE = "#00336C";
const BRAND_GREEN = "#648315";
const PAGE_BG = "#f7f9fc";

const CARD_SX = {
  border: `1.5px solid ${BRAND_BLUE}`,
  borderRadius: 2,
  boxShadow: "0 8px 20px rgba(15, 23, 42, 0.08)",
  background: "#ffffff",
  overflow: "hidden",
};

function BRECompanies({
  title = "BR&E Companies",
  currentModule = "bre",
  listRoute = "/bre-companies",
  detailRouteBase = "/bre-companies",
  addActionLabel = "Add BR&E Company",
  searchPlaceholder = "Search Company Name, Sector, NAICS or Location…",
  showModuleSwitcher = true,
  apiEndpoint = "/user-companies",
  exportFileName = "UserCompanies.xlsx",
} = {}) {
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [allCompanies, setAllCompanies] = useState([]);

  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 300);
  const [searchParams] = useSearchParams();

  const [filters, setFilters] = useState({
    industry: [],
    sector: [],
    city: "",
    county: "",
    employees: "",
    pipeline_stage: "",
    lease_expiring: "",
  });

  const getCompanyId = useCallback((company) => company.id, []);

  useEffect(() => {
    const pipelineStage = normalizePipelineStageFilter(
      searchParams.get("pipeline_stage"),
    );
    const leaseExpiring = searchParams.get("lease_expiring") || "";
    setFilters((prev) =>
      prev.pipeline_stage === pipelineStage &&
      prev.lease_expiring === leaseExpiring
        ? prev
        : {
            ...prev,
            pipeline_stage: pipelineStage,
            lease_expiring: leaseExpiring,
          },
    );
    setPage(1);
  }, [searchParams]);

  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [orderBy, setOrderBy] = useState("name");
  const [orderDirection, setOrderDirection] = useState("asc");

  const navigate = useNavigate();
  const { setRefreshPipeline } = useOutletContext() || {};

  const [importOpen, setImportOpen] = useState(false);
  const [actionsAnchorEl, setActionsAnchorEl] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const actionsMenuOpen = Boolean(actionsAnchorEl);

  const showSnackbar = useCallback((message, severity = "success") => {
    setSnackbarMsg(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  }, []);

  const loadAllCompanies = useCallback(async () => {
    try {
      const response = await api.get(`${apiEndpoint}/all`);
      setAllCompanies(Array.isArray(response.data) ? response.data : []);
    } catch {
      setAllCompanies([]);
    }
  }, [apiEndpoint]);

  const { companies, totalCount, error, refetch } = useCompaniesData({
    page,
    pageSize,
    searchTerm: debouncedSearchTerm,
    filters,
    orderBy,
    orderDirection,
    endpoint: `${apiEndpoint}/`,
  });

  useEffect(() => {
    loadAllCompanies();
  }, [loadAllCompanies]);

  const handleUpdateStage = useCallback(
    (companyId, stage) => {
      updateBRECompanyPipelineStage(companyId, stage, apiEndpoint)
        .then(() => {
          refetch();
          if (setRefreshPipeline) setRefreshPipeline((prev) => !prev);
        })
        .catch(() => showSnackbar("Failed to update company status.", "error"));
    },
    [apiEndpoint, refetch, setRefreshPipeline, showSnackbar],
  );

  const handleOpenProfile = useCallback((company) => {
    setSelectedCompany(company);
    setProfileOpen(true);
  }, []);

  const handleCloseProfile = useCallback(() => {
    setProfileOpen(false);
    setSelectedCompany(null);
  }, []);

  const handleFilterChange = useCallback((key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  }, []);

  const handleExport = useCallback(() => {
    exportBRECompanies(apiEndpoint)
      .then((response) => downloadBlobFile(response.data, exportFileName))
      .catch(() => showSnackbar("Export failed. Please try again.", "error"));
  }, [apiEndpoint, exportFileName, showSnackbar]);

  const handleCloseActionsMenu = useCallback(
    () => setActionsAnchorEl(null),
    [],
  );

  const uniqueValues = useCallback(
    (key) => {
      const normalizerByKey = {
        city: normalizeCity,
        county: normalizeString,
        sector: normalizeString,
        industry: normalizeString,
      };
      const normalizer = normalizerByKey[key] || ((v) => v);
      const source =
        key === "sector" || key === "industry" ? allCompanies : companies;
      return [
        ...new Set(
          source
            .map((c) => normalizer(c?.[key]))
            .filter((v) => v != null && v !== ""),
        ),
      ].sort((a, b) => String(a).localeCompare(String(b)));
    },
    [allCompanies, companies],
  );

  const handleSortRequest = useCallback(
    (key, explicitDirection = null) => {
      if (explicitDirection) {
        setOrderBy(key);
        setOrderDirection(explicitDirection);
      } else {
        const isAsc = orderBy === key && orderDirection === "asc";
        setOrderBy(key);
        setOrderDirection(isAsc ? "desc" : "asc");
      }
      setPage(1);
    },
    [orderBy, orderDirection],
  );

  const companyProfileSections = useMemo(
    () => (selectedCompany ? companyProfileConfig(selectedCompany) : []),
    [selectedCompany],
  );

  const {
    selectedIds,
    selectedCount,
    allSelected,
    someSelected,
    toggleOne,
    togglePage,
    setSelectedIds,
    clear: clearSelection,
  } = useRowSelection(companies, getCompanyId);

  const doDeleteCompanies = useCallback(
    async (ids) => {
      setDeleting(true);
      const results = await Promise.allSettled(
        ids.map((id) => api.delete(`${apiEndpoint}/${id}`)),
      );
      const succeeded = results.filter((r) => r.status === "fulfilled").length;
      const failed = results.length - succeeded;

      if (succeeded > 0)
        showSnackbar(
          `Deleted ${succeeded} compan${succeeded === 1 ? "y" : "ies"} successfully.`,
        );
      if (failed > 0)
        showSnackbar(`Deleted ${succeeded}, but ${failed} failed.`, "warning");

      setDeleting(false);
      setDeleteConfirmOpen(false);

      try {
        await refetch();
        await loadAllCompanies();
        setSelectedIds(new Set());
      } catch {
        showSnackbar(
          "Companies were deleted, but the table failed to refresh.",
          "warning",
        );
      }
    },
    [apiEndpoint, loadAllCompanies, refetch, setSelectedIds, showSnackbar],
  );

  const handleDeleteSelected = useCallback(() => {
    if (selectedIds.size === 0) {
      showSnackbar("No rows selected.", "info");
      return;
    }
    setDeleteConfirmOpen(true);
  }, [selectedIds, showSnackbar]);

  const confirmDeleteSelected = useCallback(async () => {
    await doDeleteCompanies(Array.from(selectedIds));
  }, [doDeleteCompanies, selectedIds]);

  const columns = useMemo(
    () => [
      { id: "name", label: "Company", sortable: true },
      { id: "location", label: "Location", sortable: false },
      { id: "sector", label: "Sector", sortable: true },
      { id: "industry", label: "NAICS Text", sortable: true },
      { id: "explore", label: "Explore", sortable: false },
    ],
    [],
  );

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100vh",
        backgroundColor: PAGE_BG,
        pb: 4,
      }}
    >
      {/* ===== Sticky Header ===== */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 5,
          borderBottom: "1px solid #dce4ef",
          background: "#ffffff",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            px: { xs: 2, md: 3 },
            py: 1.75,
            flexWrap: "wrap",
          }}
        >
          {/* Left: icon + title */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              flexShrink: 0,
            }}
          >
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: 2,
                background: BRAND_GREEN,
                color: "#ffffff",
                display: "grid",
                placeItems: "center",
                flex: "0 0 auto",
              }}
            >
              <BusinessIcon sx={{ fontSize: 19 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              {showModuleSwitcher ? (
                <CompaniesModuleSwitcher current={currentModule} />
              ) : (
                <Typography
                  variant="h6"
                  fontWeight={700}
                  sx={{ color: BRAND_BLUE, px: 1 }}
                >
                  {title}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Center: search */}
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <SearchBar
              value={searchTerm}
              onChange={(v) => {
                setSearchTerm(v);
                setPage(1);
              }}
              placeholder={searchPlaceholder}
              maxWidth={1700}
            />
          </Box>

          {/* Right: actions */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexShrink: 0,
              flexWrap: "wrap",
            }}
          >
            <Button
              endIcon={<KeyboardArrowDownIcon />}
              onClick={(e) => setActionsAnchorEl(e.currentTarget)}
              sx={ACTIONS_HEADER_BTN_SX}
            >
              Actions
            </Button>
            <Menu
              anchorEl={actionsAnchorEl}
              open={actionsMenuOpen}
              onClose={handleCloseActionsMenu}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              PaperProps={{ sx: ACTION_MENU_PAPER_SX }}
            >
              <MenuItem
                onClick={() => {
                  handleCloseActionsMenu();
                  navigate(`${listRoute}/new`);
                }}
              >
                <ListItemIcon sx={{ minWidth: 32, color: BRAND_GREEN }}>
                  <AddIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>{addActionLabel}</ListItemText>
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleCloseActionsMenu();
                  handleExport();
                }}
                disabled={companies.length === 0}
              >
                <ListItemIcon sx={{ minWidth: 32, color: BRAND_BLUE }}>
                  <FileDownloadIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Export</ListItemText>
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleCloseActionsMenu();
                  clearSelection();
                }}
                disabled={selectedCount === 0}
              >
                <ListItemIcon sx={{ minWidth: 32, color: "#B7791F" }}>
                  <ClearAllIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Clear Selection</ListItemText>
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleCloseActionsMenu();
                  handleDeleteSelected();
                }}
                disabled={selectedCount === 0}
              >
                <ListItemIcon sx={{ minWidth: 32, color: "#D32F2F" }}>
                  <DeleteForeverIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>{`Delete Selected (${selectedCount})`}</ListItemText>
              </MenuItem>
            </Menu>
          </Box>
        </Box>
      </Box>

      {/* ===== Content Area ===== */}
      <Box sx={{ px: { xs: 2, md: 3 }, mt: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Card sx={CARD_SX}>
          <BRECompaniesTable
            companies={companies}
            columns={columns}
            orderBy={orderBy}
            orderDirection={orderDirection}
            onSort={handleSortRequest}
            selectedIds={selectedIds}
            allSelected={allSelected}
            someSelected={someSelected}
            toggleOne={toggleOne}
            togglePage={togglePage}
            navigate={navigate}
            detailRouteBase={detailRouteBase}
            handleOpenProfile={handleOpenProfile}
            handleUpdateStage={handleUpdateStage}
            filters={filters}
            onFilterChange={handleFilterChange}
            uniqueValues={uniqueValues}
            PIPELINE_STAGE_OPTIONS={PIPELINE_STAGE_OPTIONS}
          />
        </Card>
        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
          <BrandPagination
            page={page}
            count={Math.ceil(totalCount / pageSize) || 1}
            onChange={(_e, value) => setPage(value)}
          />
        </Box>
      </Box>

      <EntityProfile
        open={profileOpen}
        onClose={handleCloseProfile}
        entity={selectedCompany}
        sections={companyProfileSections}
        dialogPaperSx={{
          borderRadius: "16px",
          overflow: "hidden",
          backgroundColor: BRAND_BLUE,
          border: `1px solid ${BRAND_BLUE}`,
        }}
      />

      <CompanyImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onDone={async () => {
          await refetch();
          await loadAllCompanies();
        }}
      />

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert
          severity={snackbarSeverity}
          onClose={() => setSnackbarOpen(false)}
        >
          {snackbarMsg}
        </Alert>
      </Snackbar>

      <DeleteConfirmDialog
        open={deleteConfirmOpen}
        onClose={() => !deleting && setDeleteConfirmOpen(false)}
        onConfirm={confirmDeleteSelected}
        loading={deleting}
        title="Delete Companies"
        description={`Are you sure you want to delete ${selectedIds.size} compan${selectedIds.size === 1 ? "y" : "ies"} from your CRM? This action can't be undone.`}
      />
    </Box>
  );
}

export default BRECompanies;
